/**
 * Per-build breakdown list shared by the Dashboard "App versions" card and the
 * App versions page: one row per (channel, build) with a proportional bar.
 */
import { Badge } from './ui';
import { formatIndianNumber } from '../lib/format';
import type { AppVersionRow, AppVersionState } from '../lib/analytics';

/** "v1.4.2 · build 7 · Play Store" / "Browser" / "Not recorded yet". */
export function versionLabel(row: AppVersionRow): string {
  if (row.channel === 'unknown') return 'Not recorded yet';
  if (row.channel === 'web') return 'Browser';
  const where = row.channel === 'play' ? 'Play Store' : 'Sideloaded APK';
  const ver = row.version ? `v${row.version} · ` : '';
  return row.build !== undefined ? `${ver}build ${row.build} · ${where}` : where;
}

export const STATE_META: Record<
  AppVersionState,
  { label: string; tone: 'success' | 'warn' | 'neutral' | 'brand' }
> = {
  latest: { label: 'Updated', tone: 'success' },
  behind: { label: 'Needs update', tone: 'warn' },
  sideload: { label: 'Sideloaded APK', tone: 'brand' },
  web: { label: 'Browser', tone: 'neutral' },
  unknown: { label: 'Not recorded', tone: 'neutral' },
};

export function StateBadge({ state }: { state: AppVersionState }): JSX.Element {
  const meta = STATE_META[state];
  return <Badge tone={meta.tone}>{meta.label}</Badge>;
}

export function AppVersionBreakdown({
  rows,
  total,
  selectedKey,
  onSelect,
}: {
  rows: AppVersionRow[];
  /** Denominator for the bars — normally every account. */
  total: number;
  /** When given, rows become buttons and the selected one is highlighted. */
  selectedKey?: string | null;
  onSelect?: (key: string | null) => void;
}): JSX.Element {
  return (
    <ul className="divide-y divide-slate-100">
      {rows.map((row) => {
        const pct = total === 0 ? 0 : Math.round((row.count / total) * 100);
        const selected = selectedKey === row.key;
        const inner = (
          <>
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-slate-700">{versionLabel(row)}</span>
              {row.latest && <Badge tone="success">latest</Badge>}
            </div>
            <div className="flex flex-shrink-0 items-center gap-3">
              <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${row.latest ? 'bg-emerald-500' : 'bg-brand-400'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="w-8 text-right tabular-nums font-medium text-slate-900">
                {formatIndianNumber(row.count)}
              </span>
            </div>
          </>
        );
        return (
          <li key={row.key} className="text-sm">
            {onSelect ? (
              <button
                type="button"
                onClick={() => onSelect(selected ? null : row.key)}
                title={selected ? 'Show all dealers' : 'Show only these dealers'}
                className={`flex w-full items-center justify-between gap-4 rounded-md px-2 py-2.5 text-left transition-colors hover:bg-slate-50 ${
                  selected ? 'bg-brand-50 ring-1 ring-brand-200' : ''
                }`}
              >
                {inner}
              </button>
            ) : (
              <div className="flex items-center justify-between gap-4 py-2.5">{inner}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
